import Box from '@mui/material/Box'
import Chip from '@mui/material/Chip'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { ArrowFatRight as ArrowFatRightIcon } from '@phosphor-icons/react/dist/ssr/ArrowFatRight'
import { Package as PackageIcon } from '@phosphor-icons/react/dist/ssr/Package'

import { CloudinaryImageThumbnail } from '@/components/core/thumbnail'

export interface ProductSwapCardProps {
	slotLabel?: string | null
	fromName: string | null
	fromPhoto?: string | null
	toName: string | null
	toPhoto?: string | null
	fromRevenueCents?: number | null
	toRevenueCents?: number | null
	currency?: string
	rationale?: string | null
}

function formatCents(cents: number | null | undefined, currency = 'USD'): string | null {
	if (cents == null || !Number.isFinite(cents)) return null
	return (cents / 100).toLocaleString(undefined, { currency, style: 'currency' })
}

function ProductTile({
	name,
	photo,
	revenue,
	emphasis,
}: {
	name: string | null
	photo?: string | null
	revenue?: string | null
	emphasis?: 'to'
}) {
	return (
		<Stack
			alignItems="center"
			spacing={0.5}
			sx={{
				border: '1px solid',
				borderColor: emphasis === 'to' && name ? 'rgba(var(--mui-palette-success-mainChannel) / 0.45)' : 'divider',
				borderRadius: 1,
				borderStyle: name ? 'solid' : 'dashed',
				flex: 1,
				minWidth: 0,
				px: 0.75,
				py: 0.75,
				textAlign: 'center',
			}}
		>
			{name ? (
				<CloudinaryImageThumbnail pad photo={photo ?? undefined} size={40} />
			) : (
				<Box
					sx={{
						alignItems: 'center',
						color: 'text.secondary',
						display: 'flex',
						height: 40,
						justifyContent: 'center',
						width: 40,
					}}
				>
					<PackageIcon size={20} />
				</Box>
			)}
			<Box sx={{ minWidth: 0 }}>
				<Typography
					sx={{
						display: '-webkit-box',
						fontSize: '0.75rem',
						fontWeight: 600,
						lineHeight: 1.3,
						overflow: 'hidden',
						WebkitBoxOrient: 'vertical',
						WebkitLineClamp: 2,
						wordBreak: 'break-word',
					}}
					variant="body2"
				>
					{name ?? 'Empty slot'}
				</Typography>
				{revenue ? (
					<Typography color="text.secondary" sx={{ fontSize: '0.6875rem' }} variant="caption">
						{revenue}
					</Typography>
				) : null}
			</Box>
		</Stack>
	)
}

export function ProductSwapCard({
	slotLabel,
	fromName,
	fromPhoto,
	toName,
	toPhoto,
	fromRevenueCents,
	toRevenueCents,
	currency,
	rationale,
}: ProductSwapCardProps) {
	const fromRevenue = formatCents(fromRevenueCents, currency)
	const toRevenue = formatCents(toRevenueCents, currency)
	const deltaCents = fromRevenueCents != null && toRevenueCents != null ? toRevenueCents - fromRevenueCents : null

	return (
		<Box>
			{slotLabel || (deltaCents != null && deltaCents !== 0) ? (
				<Stack alignItems="center" direction="row" justifyContent="space-between" spacing={1} sx={{ mb: 0.25 }}>
					{slotLabel ? (
						<Chip label={slotLabel} size="small" sx={{ fontSize: '0.6875rem', fontWeight: 600, height: 22 }} />
					) : (
						<Box />
					)}
					{deltaCents != null && deltaCents !== 0 ? (
						<Typography
							color={deltaCents > 0 ? 'success.main' : 'error.main'}
							sx={{ fontSize: '0.6875rem', fontWeight: 600 }}
							variant="caption"
						>
							{deltaCents > 0 ? '+' : ''}
							{formatCents(deltaCents, currency)}
						</Typography>
					) : null}
				</Stack>
			) : null}
			<Stack
				alignItems="center"
				direction="row"
				role="img"
				spacing={0.75}
				aria-label={`Swap ${fromName ?? 'empty slot'} for ${toName ?? 'empty slot'}`}
			>
				<ProductTile name={fromName} photo={fromPhoto} revenue={fromRevenue} />
				<Box aria-hidden sx={{ color: 'primary.main', display: 'flex', flexShrink: 0 }}>
					<ArrowFatRightIcon size={26} weight="fill" />
				</Box>
				<ProductTile emphasis="to" name={toName} photo={toPhoto} revenue={toRevenue} />
			</Stack>
			{rationale ? (
				<Typography color="text.secondary" sx={{ display: 'block', fontSize: '0.6875rem', mt: 0.5 }} variant="caption">
					{rationale}
				</Typography>
			) : null}
		</Box>
	)
}
